import React, { useEffect, useMemo, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import { Box, Button, Container, Link, Stack, TextField } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Copyright from '@components/Auth/Copyright';
import { yupResolver } from '@hookform/resolvers/yup';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import UserService, { LoginFormValues } from '@api/Users/UserCtrl';
import logoCircle from '@assets/img/logoCircle.png';
import logo from '@assets/logos/logo.png';
import PinInput from 'react-pin-input';

const loginSchema = Yup.object().shape({
    email: Yup.string().required('Required'),
    password: Yup.string().required('Required').min(6)
});

const LoginScreen = () => {
    const navigation = useNavigate();
    const userService = useMemo(() => new UserService(), []);
    // Mirror of the PIN value, used to feed a hidden <input type="password"> so the
    // browser's password manager can save/autofill the credential.
    const [password, setPassword] = useState('');
    // Bumping this key remounts the PinInput so it can pick up an autofilled value
    // (react-pin-input only reads `initialValue` on mount).
    const [pinKey, setPinKey] = useState(0);

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm<LoginFormValues>({
        resolver: yupResolver(loginSchema)
    });

    const onLogin: SubmitHandler<LoginFormValues> = async (values) => {
        showLoader();
        try {
            await userService.onLogin(values);
            // onAuthStateChanged hydrates the session into Redux and AppRouter
            // redirects to the profile home automatically — no manual reload needed.
        } catch (error) {
            console.log(error);
            showAlertError(error);
            hideLoader();
        }
    };

    const onForgotPassword = () => {
        navigation('/forgotPassword');
    };

    useEffect(() => {
        hideLoader();
    }, []);

    return (
        <Grid container component="main" height="100vh">
            <CssBaseline />
            <Grid item xs={0} md={6} bgcolor="black">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                    <Box component="img" src={logoCircle} width="50%" />
                </Box>
            </Grid>
            <Grid item xs={12} md={6} display="flex" alignItems="center">
                <Container maxWidth="xs">
                    <form id="form-login" onSubmit={handleSubmit(onLogin)}>
                        <Stack spacing={4} alignItems="center">
                            <Box component="img" alignSelf="center" src={logo} height={120} />
                            <Box width={354}>
                                <Controller
                                    name="email"
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            autoFocus
                                            type="email"
                                            name="username"
                                            variant="outlined"
                                            label="Correo"
                                            autoComplete="username"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.email}
                                            helperText={errors.email && errors.email.message}
                                        />
                                    )}
                                />
                            </Box>
                            <Box width={354}>
                                <Controller
                                    name="password"
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange } }) => (
                                        <>
                                            <PinInput
                                                key={pinKey}
                                                length={6}
                                                initialValue={password}
                                                secret
                                                secretDelay={100}
                                                type="numeric"
                                                inputMode="number"
                                                onChange={(val) => {
                                                    setPassword(val);
                                                    onChange(val);
                                                }}
                                                inputStyle={{
                                                    borderColor: '#B0B0B0',
                                                    borderWidth: 2,
                                                    borderRadius: 10,
                                                    fontSize: 18,
                                                    width: 55,
                                                    height: 55,
                                                    margin: 2
                                                }}
                                                inputFocusStyle={{
                                                    borderColor: '#177AD8'
                                                }}
                                            />
                                            {/* Hidden real password field: lets the browser save the
                                                credential and, on autofill, pushes the value back into
                                                the PIN boxes (by remounting them with initialValue). */}
                                            <input
                                                type="password"
                                                name="password"
                                                autoComplete="current-password"
                                                tabIndex={-1}
                                                aria-hidden="true"
                                                value={password}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                    setPassword(val);
                                                    onChange(val);
                                                    setPinKey((k) => k + 1);
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    width: 1,
                                                    height: 1,
                                                    opacity: 0,
                                                    pointerEvents: 'none'
                                                }}
                                            />
                                        </>
                                    )}
                                />
                            </Box>
                            <Link alignSelf="flex-end" href="#" variant="body1" onClick={onForgotPassword}>
                                ¿Olvidaste tu PIN?
                            </Link>
                            <Button
                                form="form-login"
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                            >
                                Entrar
                            </Button>
                            <Copyright />
                        </Stack>
                    </form>
                </Container>
            </Grid>
        </Grid>
    );
};
export default LoginScreen;
